import { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LottieAnimation } from '@shared/components/LottieAnimation';
import { Pagination, Table, type Column } from '@shared/components/Table';
import { SearchBar } from '@shared/components/SearchBar';
import { Snackbar } from '@shared/components/Snackbar';
import { Menu, MenuVariant, MenuSize } from '@shared/components/Menu';
import { Tooltip } from '@shared/components/Tooltip';
import { useFormList } from './useFormList';
import type { TaskFormListItem } from '../../types';
import { TASK_FORM_LABELS } from '../../constants/labels';
import noResultsAnimation from '@/assets/lottie/noResultsAnimation.json';
import myRequestsEmptyAnimation from '@/assets/lottie/noDataAnimation.json';

const CREATE_PATH = '/Formularze-zadań/Utwórz-formularz';
const VIEW_PATH = (id: string) => `/Formularze-zadań/Podgląd-Formularza/${id}`;
const EDIT_PATH = (id: string) => `/Formularze-zadań/Edytuj-Formularz/${id}`;

function formatDate(iso: string): string {
  const d = new Date(iso);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${yyyy}/${mm}/${dd}, ${hh}:${min}:${ss}`;
}

// ─── Three-dots SVG ───────────────────────────────────────────────────────────

const DotsIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
      stroke="#474A4C"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 6C12.5523 6 13 5.55228 13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5C11 5.55228 11.4477 6 12 6Z"
      stroke="#474A4C"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 20C12.5523 20 13 19.5523 13 19C13 18.4477 12.5523 18 12 18C11.4477 18 11 18.4477 11 19C11 19.5523 11.4477 20 12 20Z"
      stroke="#474A4C"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ─── FormList ─────────────────────────────────────────────────────────────────

export default function FormList() {
  const navigate = useNavigate();
  const location = useLocation();

  const [openMenuRowId, setOpenMenuRowId] = useState<string | null>(null);
  const [menuOpensUpward, setMenuOpensUpward] = useState(false);
  const [successSnackbarMessage, setSuccessSnackbarMessage] = useState('');

  const menuAnchorRef = useRef<HTMLDivElement | null>(null);
  const tableScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const MIN_SPACE_BELOW = 70;

  const {
    forms,
    loading,
    page,
    setPage,
    limit,
    totalCount,
    isNoFilterFetch,
    search,
    isSearchClearing,
    setSearch,
    limitError,
    handleLimitChange,
  } = useFormList();

  // Success snackbar after create or edit
  useEffect(() => {
    if (location.state?.navigationSuccessMessage) {
      setSuccessSnackbarMessage(
        location.state.navigationSuccessMessage as string
      );
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Close menu on outside click
  useEffect(() => {
    if (openMenuRowId === null) {
      return;
    }
    const handle = (e: MouseEvent) => {
      if (
        menuAnchorRef.current &&
        !menuAnchorRef.current.contains(e.target as Node)
      ) {
        setOpenMenuRowId(null);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [openMenuRowId]);

  const columns: Column<TaskFormListItem>[] = useMemo(
    () => [
      {
        key: 'name',
        header: TASK_FORM_LABELS.LIST_COL_NAME,
        flex: '1',
        gridTrack: 'minmax(140px, 2fr)',
        render: (row) => (
          <div className="w-full min-w-0 overflow-hidden">
            <Tooltip content={row.name}>
              <span className="text-[#181A1B] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
                {row.name}
              </span>
            </Tooltip>
          </div>
        ),
      },
      {
        key: 'form_id',
        header: TASK_FORM_LABELS.LIST_COL_ID,
        flex: '1',
        render: (row) => (
          <div className="w-full min-w-0">
            <Tooltip content={row.form_id}>
              <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
                {row.form_id}
              </span>
            </Tooltip>
          </div>
        ),
      },
      {
        key: 'description',
        header: TASK_FORM_LABELS.LIST_COL_DESC,
        flex: '1',
        gridTrack: 'minmax(140px, 2fr)',
        render: (row) =>
          row.description ? (
            <div className="w-full min-w-0 overflow-hidden">
              <Tooltip content={row.description}>
                <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
                  {row.description}
                </span>
              </Tooltip>
            </div>
          ) : (
            <span className="text-[#9B9EA0] text-sm">—</span>
          ),
      },
      {
        key: 'created_at',
        header: TASK_FORM_LABELS.LIST_COL_DATE,
        flex: '1',
        render: (row) => (
          <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
            {formatDate(row.created_at)}
          </span>
        ),
      },
      {
        key: 'id',
        header: TASK_FORM_LABELS.LIST_COL_ACTIONS,
        width: '80px',
        stickyRight: true,
        stickyRightBg: '#ffffff',
        render: (row, index) => {
          const isOpen = openMenuRowId === row.id;
          return (
            <div
              ref={isOpen ? menuAnchorRef : undefined}
              className="relative flex w-full items-center justify-center"
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const willOpen = openMenuRowId !== row.id;
                  if (willOpen) {
                    const buttonRect = (
                      e.currentTarget as HTMLButtonElement
                    ).getBoundingClientRect();
                    const containerRect =
                      tableScrollContainerRef.current?.getBoundingClientRect();
                    const visibleBottom = containerRect
                      ? containerRect.bottom
                      : window.innerHeight;
                    setMenuOpensUpward(
                      visibleBottom - buttonRect.bottom < MIN_SPACE_BELOW
                    );
                  }
                  setOpenMenuRowId((prev) => (prev === row.id ? null : row.id));
                }}
                className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 cursor-pointer"
                aria-expanded={isOpen}
                aria-haspopup="menu"
                data-testid={`action-menu-button-${index ?? 0}`}
              >
                <DotsIcon />
              </button>
              {isOpen && (
                <div
                  className={`absolute right-0 z-50 ${menuOpensUpward ? 'bottom-full mb-1' : 'top-full mt-1'}`}
                  role="presentation"
                >
                  <Menu
                    items={[
                      { id: 'VIEW', label: TASK_FORM_LABELS.LIST_ACTION_VIEW },
                      { id: 'EDIT', label: TASK_FORM_LABELS.LIST_ACTION_EDIT },
                    ]}
                    variant={MenuVariant.Light}
                    size={MenuSize.Small}
                    toolTip={false}
                    onItemClick={(itemId) => {
                      setOpenMenuRowId(null);
                      if (itemId === 'VIEW') {
                        navigate(VIEW_PATH(row.id));
                      } else if (itemId === 'EDIT') {
                        navigate(EDIT_PATH(row.id));
                      }
                    }}
                    showLeadingIcons={false}
                  />
                </div>
              )}
            </div>
          );
        },
      },
    ],
    [openMenuRowId, menuOpensUpward, navigate]
  );

  const pageOutOfRange =
    !loading && totalCount > 0 && page > Math.ceil(totalCount / limit);

  const hasNoData =
    !loading &&
    !isSearchClearing &&
    !pageOutOfRange &&
    page === 1 &&
    forms.length === 0 &&
    totalCount === 0 &&
    isNoFilterFetch &&
    !search.trim();

  return (
    <>
      <div className="w-full h-full flex flex-col p-4 bg-white shadow-[0px_2px_4px_rgba(0,0,0,0.08)] rounded gap-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between shrink-0">
          <div className="text-[#181A1B] text-[22px] font-normal leading-7">
            {TASK_FORM_LABELS.PAGE_TITLE}
          </div>
          {!hasNoData && (
            <button
              type="button"
              onClick={() => navigate(CREATE_PATH)}
              className="h-10 bg-[#2A4455] px-4 py-2.5 flex items-center justify-center rounded-lg hover:bg-[#1f3340] transition-colors cursor-pointer"
              data-testid="create-form-button"
            >
              <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
                {TASK_FORM_LABELS.BUTTON_CREATE}
              </span>
            </button>
          )}
        </div>

        {/* Search — hidden in empty state */}
        {!hasNoData && (
          <div className="shrink-0">
            <SearchBar
              value={search}
              onChange={setSearch}
              placeholder={TASK_FORM_LABELS.LIST_SEARCH_PLACEHOLDER}
            />
          </div>
        )}

        {/* Table area */}
        <div
          ref={tableScrollContainerRef}
          className="flex-1 min-h-0 overflow-x-auto overflow-y-auto"
        >
          {forms.length === 0 && !loading && !isSearchClearing ? (
            hasNoData ? (
              /* ── No Data (no forms created yet) ─────────────────────────── */
              <div className="flex flex-col items-center justify-center w-full h-full gap-6 pb-16">
                <LottieAnimation
                  animationData={myRequestsEmptyAnimation}
                  loop
                  width={300}
                  aspectRatio="1 / 1"
                />
                <div className="flex flex-col items-center gap-1 text-center">
                  <p className="text-base font-medium leading-6 tracking-[0.5px] text-[#474A4C]">
                    {TASK_FORM_LABELS.LIST_NO_DATA_TITLE}
                  </p>
                  <p className="text-base font-normal leading-6 tracking-[0.5px] text-[#474A4C]">
                    {TASK_FORM_LABELS.LIST_NO_DATA_DESC}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate(CREATE_PATH)}
                  className="h-10 bg-[#2A4455] px-4 py-2.5 cursor-pointer rounded-lg hover:bg-[#1f3340] transition-colors flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
                >
                  <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
                    {TASK_FORM_LABELS.BUTTON_CREATE}
                  </span>
                </button>
              </div>
            ) : (
              /* ── No Results (search returned nothing) ─────────────────── */
              <div className="flex flex-col items-center justify-center w-full h-full gap-3">
                <LottieAnimation
                  animationData={noResultsAnimation}
                  width="clamp(160px, 18vw, 360px)"
                  aspectRatio="432 / 350"
                />
                <div className="flex flex-col items-center gap-1">
                  <p className="text-center text-[#474A4C] text-base font-medium leading-6 tracking-[0.5px]">
                    {TASK_FORM_LABELS.LIST_NO_RESULTS_TITLE}
                  </p>
                  <p className="text-center text-[#474A4C] text-sm font-normal leading-5 tracking-[0.25px]">
                    {TASK_FORM_LABELS.LIST_NO_RESULTS_DESC}
                  </p>
                </div>
              </div>
            )
          ) : (
            /* ── Table ──────────────────────────────────────────────────── */
            <Table
              columns={columns}
              data={forms}
              loading={loading || isSearchClearing}
              loadingRows={limit}
              stickyHeader
              getRowKey={(row) => row.id}
              getStickyCellZIndex={(row, _idx, columnKey) =>
                columnKey === 'id' && openMenuRowId === row.id ? 50 : 1
              }
              tableBodyStyle={{ gap: 4 }}
            />
          )}
        </div>

        {/* Pagination */}
        {!hasNoData && totalCount > 10 && (
          <div className="shrink-0 px-4 -mx-4">
            <Pagination
              page={page}
              limit={limit}
              totalCount={totalCount}
              onPageChange={setPage}
              onLimitChange={handleLimitChange}
            />
            {limitError && (
              <p className="mt-2 text-[#E11B1B] text-sm font-medium leading-5 tracking-[0.1px]">
                {limitError}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Success snackbar after create or edit */}
      <Snackbar
        open={!!successSnackbarMessage}
        message={successSnackbarMessage}
        variant="success"
        onClose={() => setSuccessSnackbarMessage('')}
        autoHideDuration={6000}
        position="top-center"
        showCloseButton
      />
    </>
  );
}
